import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { MemberItem } from "@components/ui/blocks/MemberAssignmentBlock";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useState, useRef } from "react";
import { showNotification } from "context/notification.service";
import { ChangeUserPhotoModal } from "@components/modals/ChangeUserPhotoModal";
import { usersApi } from "@services/api/users";

const colorPalette = [
  "#EF4444", "#F97316", "#F59E0B", "#EAB308", "#84CC16", "#22C55E",
  "#10B981", "#14B8A6", "#06B6D4", "#0EA5E9", "#3B82F6", "#6366F1",
  "#8B5CF6", "#A855F7", "#D946EF", "#EC4899", "#F43F5E", "#64748B",
];

export const Account = () => {
  const userData = useDataStore((state) => state.userData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const fetchUserData = useDataStore((state) => state.fetchUserData);

  const [displayName, setDisplayName] = useState(userData?.infos.display_name || "");
  const [fullName, setFullName] = useState(userData?.infos.full_name || "");
  const [phoneNumber, setPhoneNumber] = useState(userData?.infos.phone_number || "");
  const [colorTag, setColorTag] = useState(userData?.customization.color_tag || "#3B82F6");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const signatureFileInputRef = useRef<HTMLInputElement>(null);

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userData) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({ type: "error", message: "Format invalide. Acceptés : JPG, PNG, WEBP" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La taille dépasse 5MB" });
      return;
    }

    setIsUploadingSignature(true);
    try {
      await usersApi.uploadSignature(userData.id, file);
      await fetchUserData(userData.id);
      showNotification({ type: "success", message: "Signature enregistrée avec succès" });
    } catch {
      showNotification({ type: "error", message: "Échec du téléchargement de la signature" });
    } finally {
      setIsUploadingSignature(false);
      if (signatureFileInputRef.current) signatureFileInputRef.current.value = "";
    }
  };

  const handleSave = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!userData?.id) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateUserData(userData.id, {
        infos: {
          email: userData.infos.email,
          display_name: displayName,
          full_name: fullName,
          phone_number: phoneNumber,
        },
        customization: {
          ...userData.customization,
          color_tag: colorTag,
        },
      });
      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
    } catch (error: any) {
      console.error("Failed to update user:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-fit w-full max-w-500 pb-96 mt-10 ml-10">
      {showPhotoModal && (
        <ChangeUserPhotoModal onClose={() => setShowPhotoModal(false)} />
      )}
      <div className="flex flex-col  gap-4  ">
        <div className="flex flex-col   ">
          <Text1>Compte</Text1>
          <Text2>Configurez votre compte ici.</Text2>
          <Text3>
            Gérez vos informations personnelles, vos préférences et les
            paramètres de votre compte.
          </Text3>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Photo de profil</div>
          <UserAvatar
            src={userData?.customization.photo_url}
            className="w-24 h-24 rounded-full border flex-shrink-0"
            value={userData?.customization.avatar_seed || userData?.infos.display_name}
            size={96}
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18}></Icon1>}
            label="Ajouter une photo"
            variant="large"
            onClick={() => setShowPhotoModal(true)}
          ></SecondaryButton>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Signature personnelle</div>
          <div className="text-h5 text-neutral-400">
            Votre signature sera automatiquement appliquée lors de la génération de comptes-rendus.
          </div>
          {userData?.infos.signature_id ? (
            <div className="border rounded-xl p-3 bg-neutral-50">
              <img
                src={userData.infos.signature_id}
                alt="Votre signature"
                className="max-h-24 object-contain"
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-neutral-200 rounded-xl p-6 flex items-center justify-center">
              <span className="text-h4 text-neutral-400">Aucune signature enregistrée</span>
            </div>
          )}
          <input
            ref={signatureFileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleSignatureUpload}
            className="hidden"
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18} />}
            label={
              isUploadingSignature
                ? "Téléchargement..."
                : userData?.infos.signature_id
                ? "Changer la signature"
                : "Ajouter une signature"
            }
            variant="large"
            disabled={isUploadingSignature}
            onClick={() => signatureFileInputRef.current?.click()}
          />
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="Nom affiché"
            value={displayName}
            onChange={(value) => setDisplayName(value)}
            placeholder="Marie C."
          />
          <TextField
            label="Nom complet"
            value={fullName}
            onChange={(value) => setFullName(value)}
            placeholder="Marie Curie"
          />
          <TextField
            label="Adresse e-mail"
            placeholder="marie.curie@life.com"
            value={userData?.infos.email}
            disabled={true}
          />
          <TextField
            label="Numéro de téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
          />
          <TextField
            label="UID"
            placeholder="uid_1234567890"
            value={userData?.id}
            disabled={true}
          />
          <div className="text-h4 text-neutral-400 mt-4">Drapeau personnel</div>
          <MemberItem
            memberName={userData?.infos.display_name || "Utilisateur"}
            memberColor={colorTag}
          ></MemberItem>
          <SecondaryButton
            startIcon={<Icon1 name="pinceau" size={18}></Icon1>}
            label="Changer de couleur"
            variant="large"
            onClick={() => setShowColorPicker(!showColorPicker)}
          ></SecondaryButton>
          {showColorPicker && (
            <div className="flex flex-col gap-2 mt-2">
              <div className="text-h5 text-neutral-600 font-semibold">
                Choisir une couleur
              </div>
              <div className="grid grid-cols-6 gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    onClick={() => setColorTag(color)}
                    className={`w-10 h-10 rounded-full transition-all hover:scale-110 ${
                      colorTag === color
                        ? "ring-2 ring-offset-2 ring-black"
                        : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          )}
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            onClick={handleSave}
            disabled={isSaving}
          ></Button>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <SecondaryButton label="Modifier l'adresse email"></SecondaryButton>
          <SecondaryButton label="Modifier le mot de passe"></SecondaryButton>
          <SecondaryButton
            danger={true}
            label="Supprimer le compte"
          ></SecondaryButton>
        </div>
      </div>
    </div>
  );
};
